import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { apiErrorDetail } from '../../lib/apiError'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useLabel, useRefreshApplications, type PolicyView, type ScreeningCriterion } from './shared'

/** The parameter a policy carries, from the criterion's value type and one text input. */
function parameterFor(c: ScreeningCriterion | undefined, text: string): Record<string, unknown> | undefined {
    if (!c || !text.trim()) return undefined
    if (c.value_type === 'ratio') return { min_ratio: Number(text) }
    if (c.value_type === 'number') return { min: Number(text) }
    if (c.value_type === 'enum') return { values: text.split(',').map((v) => v.trim()).filter(Boolean) }
    return undefined
}

function parameterText(p: PolicyView): string {
    const param = (p.parameter ?? {}) as { min_ratio?: number; min?: number; values?: string[] }
    if (param.min_ratio != null) return `≥ ${param.min_ratio}×`
    if (param.min != null) return `≥ ${param.min}`
    if (param.values?.length) return param.values.join(', ')
    return ''
}

/**
 * The selection policy of an asset (inherited downwards) or of one listing (an
 * override): the policies that apply here with their origin, the neutralised
 * ones with their reason, and the criteria the pack allows (spec 2026-10-25-11).
 */
export function ScreeningPolicyCard({ org, assetUid, listingUid }: { org: string; assetUid: string; listingUid?: string }) {
    const { t } = useTranslation('rentalApplications')
    const label = useLabel()
    const canRead = useCan('rental_applications.read')
    const canWrite = useCan('rental_applications.write')
    const refresh = useRefreshApplications()
    const [criterion, setCriterion] = useState('')
    const [policy, setPolicy] = useState('')
    const [param, setParam] = useState('')
    const [error, setError] = useState<string | null>(null)
    const { data: criteria } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/screening-criteria', {
        params: { path: { orgCode: org }, query: { asset_uid: assetUid } },
    }, { enabled: canRead })
    const { data: policies } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/screening-policies', {
        params: { path: { orgCode: org }, query: listingUid ? { listing_uid: listingUid } : { asset_uid: assetUid } },
    }, { enabled: canRead })
    const onError = (err: unknown) => setError(apiErrorDetail(err, t('errors.generic')))
    const onSuccess = () => {
        refresh()
        setError(null)
        setCriterion('')
        setPolicy('')
        setParam('')
    }
    const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/screening-policies', { onSuccess, onError })
    const update = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/screening-policies/{policyUid}', { onSuccess, onError })
    const remove = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/screening-policies/{policyUid}', { onSuccess, onError })
    if (!canRead) return null

    const byCode = Object.fromEntries((criteria?.criteria ?? []).map((c) => [c.code, c]))
    const effective = policies?.effective ?? []
    const neutralised = (policies?.own ?? []).filter((p) => p.neutralised_at)
    const own = new Set((policies?.own ?? []).map((p) => p.uid))
    const chosen = byCode[criterion]

    const row = (p: PolicyView, stale: boolean) => (
        <div key={p.uid} className="flex flex-wrap items-center gap-2" data-testid="screening-policy-row" data-criterion={p.criterion_code} data-policy={p.policy}>
            <span className="font-medium">{label(byCode[p.criterion_code]?.label, p.criterion_code)}</span>
            {own.has(p.uid) && canWrite && !stale ? (
                <Select value={p.policy} onValueChange={(v) => update.mutate({ params: { path: { orgCode: org, policyUid: p.uid } }, body: { policy: v } })}>
                    <SelectTrigger className="h-8 w-32" data-testid="screening-policy-row-policy"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {(byCode[p.criterion_code]?.allowed_policies ?? [p.policy]).map((v) => <SelectItem key={v} value={v}>{t(`policy.${v}`)}</SelectItem>)}
                    </SelectContent>
                </Select>
            ) : (
                <Badge variant="outline">{t(`policy.${p.policy}`)}</Badge>
            )}
            <span className="text-muted-foreground">{parameterText(p)}</span>
            {p.inherited && (
                <Badge variant="secondary" data-testid="screening-policy-inherited-badge">
                    {t('policies.inherited', { from: p.origin === 'listing' ? t('policies.listing') : p.origin_name ?? '' })}
                </Badge>
            )}
            {stale && (
                <Badge variant="warning" data-testid="screening-policy-neutralised-badge">
                    {t(`neutralised.reason.${p.neutralised_reason}`, { defaultValue: p.neutralised_reason ?? '' })}
                </Badge>
            )}
            {own.has(p.uid) && canWrite && (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate({ params: { path: { orgCode: org, policyUid: p.uid } } })} data-testid="screening-policy-delete">
                    {t('policies.delete')}
                </Button>
            )}
        </div>
    )

    return (
        <Card data-testid="screening-policy-card">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{listingUid ? t('policies.title_listing') : t('policies.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {criteria?.release?.indicative && <p className="text-xs text-muted-foreground">{t('screening.indicative')}</p>}
                {effective.length === 0 && neutralised.length === 0 && <p className="text-muted-foreground">{t('policies.none')}</p>}
                {effective.map((p) => row(p, false))}
                {neutralised.map((p) => row(p, true))}
                {canWrite && (criteria?.criteria ?? []).length > 0 && (
                    <div className="grid gap-2 border-t pt-2 md:grid-cols-4">
                        <Select value={criterion} onValueChange={(v) => { setCriterion(v); setPolicy(byCode[v]?.allowed_policies?.[0] ?? '') }}>
                            <SelectTrigger data-testid="screening-policy-criterion-select"><SelectValue placeholder={t('policies.criterion')} /></SelectTrigger>
                            <SelectContent>
                                {(criteria?.criteria ?? []).map((c) => <SelectItem key={c.code} value={c.code} data-testid={`screening-policy-criterion-${c.code}`}>{label(c.label, c.code)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={policy} onValueChange={setPolicy} disabled={!chosen}>
                            <SelectTrigger data-testid="screening-policy-select"><SelectValue placeholder={t('policies.policy')} /></SelectTrigger>
                            <SelectContent>
                                {(chosen?.allowed_policies ?? []).map((v) => <SelectItem key={v} value={v} data-testid={`screening-policy-option-${v}`}>{t(`policy.${v}`)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Input
                            value={param}
                            onChange={(e) => setParam(e.target.value)}
                            disabled={!chosen || !['ratio', 'number', 'enum'].includes(chosen.value_type)}
                            placeholder={chosen ? t(`policies.parameter_${chosen.value_type}`, { defaultValue: '' }) : ''}
                            data-testid="screening-policy-parameter"
                        />
                        <Button
                            size="sm"
                            disabled={!criterion || !policy || create.isPending}
                            onClick={() => create.mutate({
                                params: { path: { orgCode: org } },
                                body: { ...(listingUid ? { listing_uid: listingUid } : { asset_uid: assetUid }), criterion_code: criterion, policy, parameter: parameterFor(chosen, param) },
                            })}
                            data-testid="screening-policy-add"
                        >
                            {t('policies.add')}
                        </Button>
                    </div>
                )}
                {error && <p className="text-destructive" data-testid="screening-policy-error">{error}</p>}
            </CardContent>
        </Card>
    )
}
